import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CalendarClock, CheckCircle2, Loader2, Mail, Phone, Rocket } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ApiError } from '@/api/http-client';
import {
  getChamadosAdminControllerDetalharQueryKey,
  getChamadosAdminControllerListarMensagensQueryKey,
  getChamadosAdminControllerListarQueryKey,
  getChamadosAdminControllerResumoQueryKey,
  useChamadosAdminControllerAgendarCorrecao,
  useChamadosAdminControllerAssumir,
  useChamadosAdminControllerConcluir,
  useChamadosAdminControllerDefinirPrevisao,
  useChamadosAdminControllerDefinirUrgencia,
  useChamadosAdminControllerDetalhar,
  useChamadosAdminControllerEnviarAnexo,
  useChamadosAdminControllerEnviarMensagem,
  useChamadosAdminControllerListarAtendentes,
  useChamadosAdminControllerListarMensagens,
  useChamadosAdminControllerMarcarComoLido,
  useChamadosAdminControllerTransferir,
} from '@/api/generated/admin-suporte/admin-suporte';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { ComposerDeMensagem } from '@/features/suporte/components/ComposerDeMensagem';
import {
  AgendarCorrecaoDialogo,
  ConcluirChamadoDialogo,
  DefinirPrevisaoDialogo,
  TransferirChamadoDialogo,
} from '@/features/suporte/components/DialogosDoAtendente';
import { ThreadDeMensagens } from '@/features/suporte/components/ThreadDeMensagens';
import {
  CLASSE_STATUS,
  CLASSE_URGENCIA,
  estaEncerrado,
  estaVivo,
  formatarData,
  formatarDataHora,
  ROTULO_STATUS_ADMIN,
  ROTULO_URGENCIA,
  URGENCIAS,
} from '@/features/suporte/rotulos';
import { INTERVALO_CHAT_MS, MENSAGENS_NA_CONVERSA } from '@/features/suporte/suporte.constants';
import { EstadoCarregando, EstadoErro } from '@/shared/components/estados';
import { cn } from '@/shared/lib/cn';
import { ROTAS } from '@/shared/lib/rotas';
import { hasPermission } from '@/shared/permissoes/hasPermission';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { toast } from '@/shared/ui/sonner';

/** Um chamado, do ponto de vista de quem atende. */
export function ChamadoAtendimentoPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const { me } = useAuth();

  const [aguardarSolicitante, setAguardarSolicitante] = useState(false);
  const [definindoPrevisao, setDefinindoPrevisao] = useState(false);
  const [agendandoCorrecao, setAgendandoCorrecao] = useState(false);
  const [concluindo, setConcluindo] = useState(false);
  const [transferindo, setTransferindo] = useState(false);

  const {
    data: chamado,
    isPending,
    isError,
    refetch,
  } = useChamadosAdminControllerDetalhar(id, { query: { refetchInterval: INTERVALO_CHAT_MS } });

  const vivo = estaVivo(chamado?.status);

  const { data: mensagens } = useChamadosAdminControllerListarMensagens(
    id,
    { pagina: 1, porPagina: MENSAGENS_NA_CONVERSA, ordenarPor: 'criadoEm', ordem: 'desc' },
    { query: { enabled: Boolean(chamado), refetchInterval: vivo ? INTERVALO_CHAT_MS : false } },
  );

  const podeEditar = hasPermission(me?.permissoes, 'admin_suporte.editar');

  const { data: atendentes } = useChamadosAdminControllerListarAtendentes({
    query: { enabled: podeEditar },
  });

  const assumir = useChamadosAdminControllerAssumir();
  const enviarMensagem = useChamadosAdminControllerEnviarMensagem();
  const enviarAnexo = useChamadosAdminControllerEnviarAnexo();
  const marcarComoLido = useChamadosAdminControllerMarcarComoLido();
  const definirUrgencia = useChamadosAdminControllerDefinirUrgencia();
  const definirPrevisao = useChamadosAdminControllerDefinirPrevisao();
  const agendarCorrecao = useChamadosAdminControllerAgendarCorrecao();
  const concluir = useChamadosAdminControllerConcluir();
  const transferir = useChamadosAdminControllerTransferir();

  const meuId = me?.usuario.id;
  const ehMeu = Boolean(chamado?.atendenteId && chamado.atendenteId === meuId);

  // Só marca como lido depois de assumir: antes disso o atendente está só
  // espiando a fila, e criar participação por leitura faria o chamado aparecer
  // como "dele" para quem só passou o olho.
  useEffect(() => {
    if (chamado && ehMeu && chamado.naoLidas > 0) void marcarComoLido.mutateAsync({ id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, ehMeu, chamado?.naoLidas]);

  async function revalidar() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getChamadosAdminControllerDetalharQueryKey(id) }),
      queryClient.invalidateQueries({ queryKey: getChamadosAdminControllerListarQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getChamadosAdminControllerResumoQueryKey() }),
    ]);
  }

  /** Toda ação do painel faz o mesmo: roda, avisa, revalida — ou mostra o erro. */
  async function executar(acao: () => Promise<unknown>, sucesso: string, falha: string) {
    try {
      await acao();
      await revalidar();
      toast.success(sucesso);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : falha);
      throw e;
    }
  }

  async function aoEnviar({ corpo, arquivo }: { corpo: string; arquivo: File | null }) {
    try {
      const arquivoId = arquivo
        ? (await enviarAnexo.mutateAsync({ data: { arquivo } })).arquivoId
        : undefined;

      await enviarMensagem.mutateAsync({
        id,
        data: { corpo, clientMsgId: crypto.randomUUID(), arquivoId, aguardarSolicitante },
      });

      setAguardarSolicitante(false);
      await queryClient.invalidateQueries({
        queryKey: getChamadosAdminControllerListarMensagensQueryKey(id),
      });
      await revalidar();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível enviar a mensagem.');
      throw e;
    }
  }

  if (isPending) return <EstadoCarregando rotulo="Carregando chamado..." />;
  if (isError || !chamado) return <EstadoErro aoTentarDeNovo={() => void refetch()} />;

  const encerrado = estaEncerrado(chamado.status);

  return (
    <div className="space-y-6">
      <Link
        to={ROTAS.adminSuporte}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-brand"
      >
        <ArrowLeft className="h-4 w-4" />
        Fila de suporte
      </Link>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <header className="space-y-2">
            <p className="font-mono text-xs text-muted-foreground">{chamado.codigo}</p>
            <h1 className="text-xl font-semibold">{chamado.titulo}</h1>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className={cn('rounded border px-2 py-0.5', CLASSE_STATUS[chamado.status])}>
                {ROTULO_STATUS_ADMIN[chamado.status]}
              </span>
              <span className={cn('rounded border px-2 py-0.5', CLASSE_URGENCIA[chamado.urgencia])}>
                {ROTULO_URGENCIA[chamado.urgencia]}
              </span>
              <span className="text-muted-foreground">
                Aberto em {formatarDataHora(chamado.abertoEm)}
              </span>
            </div>
          </header>

          <div className="rounded-lg border border-brand/15 bg-surface-raised p-4">
            <ThreadDeMensagens
              mensagens={[...(mensagens?.itens ?? [])].reverse()}
              meuProfileId={meuId}
            />
          </div>

          {encerrado ? (
            <div className="rounded-lg border border-emerald-600/30 bg-emerald-600/5 p-4">
              <p className="flex items-center gap-2 text-sm font-medium">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                {chamado.status === 'CONCLUIDO' ? 'Chamado concluído' : 'Chamado cancelado'}
              </p>
              {chamado.resolucao && (
                <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                  {chamado.resolucao}
                </p>
              )}
            </div>
          ) : ehMeu ? (
            <ComposerDeMensagem
              aoEnviar={aoEnviar}
              enviando={enviarMensagem.isPending || enviarAnexo.isPending}
              placeholder="Responda ao solicitante..."
              extras={
                <>
                  <Checkbox
                    id="aguardar"
                    checked={aguardarSolicitante}
                    onCheckedChange={(v) => setAguardarSolicitante(v === true)}
                  />
                  <Label htmlFor="aguardar" className="cursor-pointer text-muted-foreground">
                    Aguardar resposta do solicitante
                  </Label>
                </>
              }
            />
          ) : (
            <p className="rounded-lg border border-dashed border-brand/25 p-4 text-center text-sm text-muted-foreground">
              {chamado.atendenteNome
                ? `Este chamado está com ${chamado.atendenteNome}.`
                : 'Assuma o chamado para conversar com o solicitante.'}
            </p>
          )}
        </div>

        <aside className="space-y-4">
          <div className="space-y-3 rounded-lg border border-brand/15 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand">Contato</p>
            <div className="space-y-1 text-sm">
              <p className="font-medium">{chamado.solicitanteNome}</p>
              <p className="text-xs text-muted-foreground">{chamado.tenantNome}</p>
            </div>
            {/* O telefone do formulário virando ação — é para isso que ele foi pedido. */}
            <div className="flex flex-col gap-1 text-sm">
              <a
                href={`tel:${chamado.telefoneContato}`}
                className="flex items-center gap-2 text-brand"
              >
                <Phone className="h-3.5 w-3.5" />
                {chamado.telefoneContato}
              </a>
              <a
                href={`mailto:${chamado.solicitanteEmail}`}
                className="flex items-center gap-2 break-all text-brand"
              >
                <Mail className="h-3.5 w-3.5 shrink-0" />
                {chamado.solicitanteEmail}
              </a>
            </div>
          </div>

          {!encerrado && (
            <div className="space-y-4 rounded-lg border border-brand/15 p-4">
              {chamado.status === 'ABERTO' ? (
                <Button
                  className="w-full"
                  disabled={assumir.isPending}
                  onClick={() =>
                    void executar(
                      () => assumir.mutateAsync({ id }),
                      'Chamado assumido.',
                      'Outro atendente pegou este chamado.',
                    ).catch(() => undefined)
                  }
                >
                  {assumir.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Assumir este chamado
                </Button>
              ) : null}

              {podeEditar && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-brand">
                    Urgência
                  </p>
                  <Select
                    value={chamado.urgencia}
                    onValueChange={(v) =>
                      void executar(
                        () => definirUrgencia.mutateAsync({ id, data: { urgencia: v as never } }),
                        'Urgência atualizada.',
                        'Não foi possível alterar a urgência.',
                      ).catch(() => undefined)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {URGENCIAS.map((u) => (
                        <SelectItem key={u} value={u}>
                          {ROTULO_URGENCIA[u]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {ehMeu && (
                <>
                  <BlocoDePrazo
                    icone={CalendarClock}
                    titulo="Previsão de conclusão"
                    valor={
                      chamado.previsaoConclusaoEm
                        ? formatarDataHora(chamado.previsaoConclusaoEm)
                        : null
                    }
                    observacao={chamado.previsaoObservacao}
                    aoAbrir={() => setDefinindoPrevisao(true)}
                  />

                  <BlocoDePrazo
                    icone={Rocket}
                    titulo="Correção em produção"
                    valor={
                      chamado.correcaoPrevistaEm ? formatarData(chamado.correcaoPrevistaEm) : null
                    }
                    observacao={chamado.correcaoDescricao}
                    aoAbrir={() => setAgendandoCorrecao(true)}
                  />

                  <Button className="w-full" onClick={() => setConcluindo(true)}>
                    Concluir chamado
                  </Button>
                </>
              )}

              {podeEditar && (
                <Button variant="outline" className="w-full" onClick={() => setTransferindo(true)}>
                  {ehMeu ? 'Transferir' : 'Transferir para mim ou outro'}
                </Button>
              )}
            </div>
          )}
        </aside>
      </div>

      <DefinirPrevisaoDialogo
        aberto={definindoPrevisao}
        aoFechar={() => setDefinindoPrevisao(false)}
        valorAtual={chamado.previsaoConclusaoEm}
        observacaoAtual={chamado.previsaoObservacao}
        aoSalvar={(data) =>
          executar(
            () => definirPrevisao.mutateAsync({ id, data }),
            'Previsão informada ao solicitante.',
            'Não foi possível salvar a previsão.',
          )
        }
        aoLimpar={() =>
          executar(
            () => definirPrevisao.mutateAsync({ id, data: { previsaoConclusaoEm: null } }),
            'Previsão removida.',
            'Não foi possível remover a previsão.',
          )
        }
      />

      <AgendarCorrecaoDialogo
        aberto={agendandoCorrecao}
        aoFechar={() => setAgendandoCorrecao(false)}
        valorAtual={chamado.correcaoPrevistaEm}
        descricaoAtual={chamado.correcaoDescricao}
        aoSalvar={(data) =>
          executar(
            () => agendarCorrecao.mutateAsync({ id, data }),
            'Data da correção informada ao solicitante.',
            'Não foi possível agendar a correção.',
          )
        }
        aoLimpar={() =>
          executar(
            () => agendarCorrecao.mutateAsync({ id, data: { correcaoPrevistaEm: null } }),
            'Data removida.',
            'Não foi possível remover a data.',
          )
        }
      />

      <ConcluirChamadoDialogo
        aberto={concluindo}
        aoFechar={() => setConcluindo(false)}
        aoConcluir={(resolucao) =>
          executar(
            () => concluir.mutateAsync({ id, data: { resolucao } }),
            'Chamado concluído.',
            'Não foi possível concluir o chamado.',
          )
        }
      />

      <TransferirChamadoDialogo
        aberto={transferindo}
        aoFechar={() => setTransferindo(false)}
        atendentes={atendentes?.itens ?? []}
        atendenteAtualId={chamado.atendenteId}
        aoTransferir={(data) =>
          executar(
            () => transferir.mutateAsync({ id, data }),
            'Chamado transferido.',
            'Não foi possível transferir o chamado.',
          )
        }
      />
    </div>
  );
}

function BlocoDePrazo({
  icone: Icone,
  titulo,
  valor,
  observacao,
  aoAbrir,
}: {
  icone: typeof CalendarClock;
  titulo: string;
  valor: string | null;
  observacao: string | null;
  aoAbrir: () => void;
}) {
  return (
    <div className="space-y-1.5">
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-brand">
        <Icone className="h-3.5 w-3.5" />
        {titulo}
      </p>
      {valor ? (
        <>
          <p className="text-sm font-medium">{valor}</p>
          {observacao && <p className="text-xs text-muted-foreground">{observacao}</p>}
        </>
      ) : (
        <p className="text-sm text-muted-foreground">Sem data definida</p>
      )}
      <Button variant="outline" size="sm" className="w-full" onClick={aoAbrir}>
        {valor ? 'Alterar' : 'Definir'}
      </Button>
    </div>
  );
}
